// Popup-ul „Acces examen” (personal): permisiunea pentru examenul teoretic.
// Sus filele cu numere — Elevi · Selectați · Activi (cu acces acum: „În examen” sau „Are acces”,
// din GET /exams/permissions/active; cine a terminat nu mai apare). În „Elevi”, cei cu acces stau
// primii și nu se mai selectează. Lista se încarcă singură la derulare (useAutoLoadMore), starea
// activilor se reîmprospătează la 30 s și la întoarcerea în fereastră. Jos: „Acordă permisiunea”.
// Doar tiparele universale: ChoiceUI tabs, SearchBarUI, StudentItemUI, TagUI, ButtonUI.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchStudents } from "../../store/studentsSlice";
import { getActiveExamPermissions, getMe, grantExamPermissionBulk } from "../../api/examService";
import useAutoLoadMore from "../../hooks/useAutoLoadMore";
import { ButtonUI, ChoiceUI, SearchBarUI, StudentItemUI } from "../UI";
import UIIcon from "../Common/UIIcon";
import AlertPills from "../Utils/AlertPills";

const TZ_MD = "Europe/Chisinau";
const TAB_STUDENTS = "students";
const TAB_SELECTED = "selected";
const TAB_ACTIVE = "active";
const PAGE_SIZE = 24;
const EXAM_PERMISSION_HOURS = 12;
const MAX_ATTEMPTS = 1;
const ACTIVE_REFRESH_MS = 30_000;

function fmtMd(iso) {
   if (!iso) return "-";
   return new Date(iso).toLocaleString("ro-RO", { timeZone: TZ_MD, dateStyle: "medium", timeStyle: "short" });
}

// valabilitatea, calculată ca înainte (ora Chișinăului scrisă ca UTC, + EXAM_PERMISSION_HOURS)
function partsForTZ(date, timeZone) {
   const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
   }).formatToParts(date);
   const get = (type) => Number(parts.find((part) => part.type === type)?.value);
   return { y: get("year"), m: get("month"), d: get("day"), hh: get("hour"), mm: get("minute"), ss: get("second") };
}

function validUntilFromChisinauNow(hours) {
   const { y, m, d, hh, mm, ss } = partsForTZ(new Date(), TZ_MD);
   return new Date(Date.UTC(y, m - 1, d, hh, mm, ss, 0) + hours * 3600_000).toISOString();
}

function fmtTime(iso) {
   if (!iso) return "";
   return new Date(iso).toLocaleTimeString("ro-RO", { timeZone: TZ_MD, hour: "2-digit", minute: "2-digit" });
}

function matchesQuery(record, query) {
   const normalized = String(query || "").trim().toLowerCase();
   if (!normalized) return true;
   return [record?.firstName, record?.lastName, record?.phone, record?.email]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalized);
}

const fullNameOf = (s) => `${s?.firstName || ""} ${s?.lastName || ""}`.trim();
const isStudent = (u) => String(u?.role ?? "").trim().toUpperCase() === "USER" && u?.isGuest !== true;

export default function ExamPermissionGrantPopup() {
   const dispatch = useDispatch();
   const studentsAll = useSelector((state) => state.students?.list || []);

   const [meId, setMeId] = useState(null);
   const [q, setQ] = useState("");
   const [activeTab, setActiveTab] = useState(TAB_STUDENTS);
   const [selected, setSelected] = useState(() => new Set());
   const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
   const [activeRows, setActiveRows] = useState([]);
   const [alerts, setAlerts] = useState([]);
   const [busy, setBusy] = useState(false);

   const pushAlert = useCallback(
      (type, text) => setAlerts((prev) => [...prev, { id: Date.now() + Math.random(), type, text }]),
      [],
   );
   const popAlert = () => setAlerts((prev) => prev.slice(0, -1));

   useEffect(() => {
      // lista păstrată în browser se arată imediat; o reîmprospătăm o dată, în fundal
      dispatch(fetchStudents());
   }, [dispatch]);

   useEffect(() => {
      let alive = true;
      getMe()
         .then((me) => {
            const id = Number(me?.id);
            if (alive && Number.isInteger(id) && id > 0) setMeId(id);
         })
         .catch(() => {});
      return () => {
         alive = false;
      };
   }, []);

   // cine are acces acum (în examen / încă neînceput); la 30 s și la întoarcerea în fereastră
   const loadActive = useCallback(async () => {
      try {
         setActiveRows(await getActiveExamPermissions());
      } catch {
         // fără lista activilor popup-ul merge mai departe (doar acordarea)
      }
   }, []);
   useEffect(() => {
      loadActive();
      const timer = window.setInterval(loadActive, ACTIVE_REFRESH_MS);
      const onFocus = () => loadActive();
      window.addEventListener("focus", onFocus);
      return () => {
         window.clearInterval(timer);
         window.removeEventListener("focus", onFocus);
      };
   }, [loadActive]);

   const activeById = useMemo(() => new Map(activeRows.map((row) => [Number(row.userId), row])), [activeRows]);
   const students = useMemo(() => (studentsAll || []).filter(isStudent), [studentsAll]);
   const studentsById = useMemo(() => new Map(students.map((s) => [Number(s.id), s])), [students]);

   // „Elevi”: cei cu acces primii (întâi cei în examen), apoi ceilalți după nume
   const studentRows = useMemo(() => {
      const rank = (s) => {
         const a = activeById.get(Number(s.id));
         return a ? (a.state === "in_exam" ? 0 : 1) : 2;
      };
      return students
         .filter((s) => matchesQuery(s, q))
         .sort((a, b) => rank(a) - rank(b) || fullNameOf(a).localeCompare(fullNameOf(b), "ro"));
   }, [students, activeById, q]);

   const selectedRows = useMemo(
      () => students.filter((s) => selected.has(Number(s.id)) && matchesQuery(s, q)),
      [students, selected, q],
   );

   const activeList = useMemo(
      () =>
         activeRows
            .map((row) => ({ ...(studentsById.get(Number(row.userId)) || {}), ...row, id: Number(row.userId) }))
            .filter((s) => matchesQuery(s, q))
            .sort((a, b) => (a.state === b.state ? fullNameOf(a).localeCompare(fullNameOf(b), "ro") : a.state === "in_exam" ? -1 : 1)),
      [activeRows, studentsById, q],
   );

   const rows = activeTab === TAB_SELECTED ? selectedRows : activeTab === TAB_ACTIVE ? activeList : studentRows;
   const visibleRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount]);

   useEffect(() => {
      setVisibleCount(PAGE_SIZE);
   }, [activeTab, q]);

   // fără „Afișează încă”: următorii vin singuri aproape de capătul listei
   const autoLoad = useAutoLoadMore(visibleCount < rows.length, () =>
      setVisibleCount((c) => Math.max(c, Math.min(visibleCount + PAGE_SIZE, rows.length))),
   );

   const toggleOne = useCallback(
      (id) => {
         if (activeById.has(id)) return; // are deja acces
         setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
         });
      },
      [activeById],
   );

   async function handleGrant() {
      if (selected.size === 0) {
         pushAlert("error", "Selectează cel puțin un elev.");
         return;
      }
      if (!Number.isInteger(meId) || meId <= 0) {
         pushAlert("error", "Nu am putut afla cine acordă permisiunea.");
         return;
      }
      try {
         setBusy(true);
         const validUntil = validUntilFromChisinauNow(EXAM_PERMISSION_HOURS);
         const res = await grantExamPermissionBulk({
            userIds: Array.from(selected).map(Number),
            validUntil,
            maxAttempts: MAX_ATTEMPTS,
            grantedById: meId,
         });
         const successIds = Array.isArray(res?.successIds) ? res.successIds.map(Number) : [];
         const failed = Array.isArray(res?.failed) ? res.failed : [];
         setSelected(new Set());
         if (successIds.length) {
            const names = successIds.map((id) => fullNameOf(studentsById.get(id)) || `#${id}`);
            const extra = names.length > 5 ? ` +${names.length - 5}` : "";
            pushAlert("success", `Acces acordat: ${names.slice(0, 5).join(", ")}${extra}. Până la ${fmtMd(validUntil)}.`);
            setActiveTab(TAB_ACTIVE);
         }
         for (const f of failed) {
            const id = Number(f?.userId);
            pushAlert("error", `${fullNameOf(studentsById.get(id)) || `#${id}`}: ${f?.error || "eroare necunoscută"}`);
         }
         await loadActive();
      } catch (error) {
         pushAlert("error", error?.message || "Nu am putut acorda permisiunea.");
      } finally {
         setBusy(false);
      }
   }

   const renderRow = (student) => {
      const id = Number(student?.id);
      const active = activeById.get(id);
      const checked = selected.has(id);
      // cu acces: starea pe rândul de sub nume (numele rămâne întreg); fără: bifa alegerii
      const statusLine = active
         ? active.state === "in_exam"
            ? `În examen · de la ${fmtTime(active.examStartedAt)}`
            : `Are acces · până la ${fmtTime(active.validUntil)}`
         : null;
      const status = active ? null : (
         <span className={"examPermissionPopup__check" + (checked ? " is-on" : "")} aria-hidden="true">
            {checked ? <UIIcon name="check" className="examPermissionPopup__checkIcon" /> : null}
         </span>
      );
      return (
         <StudentItemUI
            key={id}
            student={student}
            onOpen={active ? undefined : () => toggleOne(id)}
            highlightQuery={q}
            secondaryText={statusLine || student?.phone || student?.email || "–"}
            showChevron={false}
            endAdornment={status}
            className={
               "examPermissionPopup__row" + (checked ? " is-selected" : "") + (active ? ` is-${active.state}` : "")
            }
         />
      );
   };

   const inExamCount = activeRows.filter((r) => r.state === "in_exam").length;
   const emptyText =
      activeTab === TAB_SELECTED
         ? "Niciun elev selectat."
         : activeTab === TAB_ACTIVE
           ? "Nimeni nu are acces acum."
           : "Niciun elev găsit.";

   return (
      <>
         <section className="examPermissionPopup">
            <div className="examPermissionPopup__header">
               <h3 className="examPermissionPopup__title">Acces examen</h3>
            </div>

            <ChoiceUI
               look="tabs"
               className="examPermissionPopup__tabs"
               aria-label="Elevii"
               value={activeTab}
               onChange={setActiveTab}
               options={[
                  { value: TAB_STUDENTS, label: "Elevi", count: students.length || undefined },
                  { value: TAB_SELECTED, label: "Selectați", count: selected.size || undefined },
                  {
                     value: TAB_ACTIVE,
                     label: "Activi",
                     count: activeRows.length || undefined,
                     title: inExamCount ? `${inExamCount} în examen acum` : "Cu acces acum",
                  },
               ]}
            />

            <SearchBarUI
               className="examPermissionPopup__searchBar"
               placeholder="Caută după nume, telefon sau email…"
               value={q}
               onChange={(event) => setQ(event.target.value)}
               onClear={() => setQ("")}
            />

            <div className="examPermissionPopup__listWrapper">
               <div className="examPermissionPopup__items" ref={autoLoad.ref} onScroll={autoLoad.onScroll}>
                  {visibleRows.map(renderRow)}
                  {!rows.length ? <p className="examPermissionPopup__empty">{emptyText}</p> : null}
               </div>
            </div>

            <ButtonUI.Row className="examPermissionPopup__actions">
               {selected.size ? <ButtonUI onClick={() => setSelected(new Set())} disabled={busy}>Golește</ButtonUI> : null}
               <ButtonUI variant="primary" onClick={handleGrant} disabled={!selected.size || busy} loading={busy}>
                  {busy ? "Se acordă…" : `Acordă permisiunea${selected.size ? ` · ${selected.size}` : ""}`}
               </ButtonUI>
            </ButtonUI.Row>
         </section>

         <AlertPills messages={alerts} onDismiss={popAlert} />
      </>
   );
}
